import { useCallback } from "react";
import * as amplitude from "@amplitude/analytics-browser";
import * as Sentry from "@sentry/react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { CurrentUser } from "sdk/currentUser";
import { useLazyCurrentUserIndexQuery } from "~/store/api";
import { signOut, setCurrentUser } from "~/store/authentication";
import { useAppDispatch } from "~/store/hooks";

interface UseAuthN {
  handleSetUser: (user: CurrentUser) => void;
  handleUserSignOut: () => void;
  refreshCurrentUser: () => Promise<void>;
}
const identifyEvent = new amplitude.Identify();

export const useAuthN = (): UseAuthN => {
  const dispatch = useAppDispatch();
  const [apiCurrentUserIndex] = useLazyCurrentUserIndexQuery();
  const ldClient = useLDClient();

  const handleSetUser = useCallback(
    (user: CurrentUser) => {
      dispatch(setCurrentUser(user));

      ldClient?.identify({ key: user.externalId });

      amplitude.setUserId(user.externalId);
      const { profilePicture, referralToken, ...track_vals } = user;
      for (const attr in track_vals) {
        const initial = track_vals[attr as keyof typeof track_vals];
        const val =
          typeof initial == "object" ? JSON.stringify(initial) : initial;
        identifyEvent.set(attr, val);
      }
      amplitude.identify(identifyEvent);

      Sentry.setUser(user);
    },
    [dispatch, ldClient],
  );

  const handleUserSignOut = useCallback((): void => {
    // These are to ensure that Sentry and Intercom do not remember the user
    Sentry.setUser(null);
    amplitude.reset();

    dispatch(signOut());
  }, [dispatch]);

  // This will always be needed as long as we need results from TypeForm. This
  // is because on submit, we call TypeForm to get the User's response, then
  // set certain things from that.
  const refreshCurrentUser = useCallback(async () => {
    try {
      const responseBody = await apiCurrentUserIndex().unwrap();
      const currentUser = responseBody.data.attributes;
      handleSetUser(currentUser);
    } catch (e: any) {
      Sentry.captureException(e);
    }
  }, [apiCurrentUserIndex, handleSetUser]);

  return {
    handleSetUser,
    handleUserSignOut,
    refreshCurrentUser,
  };
};
